import * as THREE from "three";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import canvasManager from "./canvas-manager";
import { VISIBILITY_MODES } from "./model-state";
import { isPortrait } from "./scene-utils";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const fallbackIprData = [];

let iprLayers = null;

export const setIprLayers = (layers) => {
  iprLayers = layers || null;
};

/**
 * Get all layers from iprLayers sorted by layer number (1, 2, 3, …).
 * Works with any key format that contains a number, e.g. "layer1", "layer2", "3", etc.
 * @returns {{ key: string, data: object, num: number }[]}
 */
const getSortedLayers = () => {
  if (!iprLayers || typeof iprLayers !== "object") return [];
  return Object.entries(iprLayers)
    .map(([key, data]) => {
      const match = key.match(/(\d+)/);
      return match ? { key, data, num: parseInt(match[1], 10) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.num - b.num);
};

const normalizeAlignerNumber = (alignerNum) => {
  if (alignerNum === null || alignerNum === undefined) return null;
  const parsed = parseInt(alignerNum, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/** IPR starts from aligner 1; treat layer startAligner 0 as 1 for range and display. */
const iprEffectiveStart = (start) => (start === 0 ? 1 : start);

const getCurrentAlignerNumber = (upperAlignerNum, lowerAlignerNum) => {
  const upper = normalizeAlignerNumber(upperAlignerNum);
  const lower = normalizeAlignerNumber(lowerAlignerNum);
  if (upper === null) return lower;
  if (lower === null) return upper;
  return Math.max(upper, lower);
};

const getIprEntriesForAligner = (alignerNum) => {
  if (!iprLayers) return null;
  const current = normalizeAlignerNumber(alignerNum);
  if (current === null) return null;

  const layers = getSortedLayers();

  // IPR starts from aligner 1; no IPR at aligner 0
  if (current === 0) return [];

  // Find the layer whose aligner range contains the current aligner (start 0 treated as 1)
  for (const { data } of layers) {
    if (data == null) continue;
    const effectiveStart = iprEffectiveStart(data.startAligner ?? 0);
    if (current >= effectiveStart && current <= data.endAligner) {
      return data.ipr || [];
    }
  }

  return [];
};

/**
 * Get ALL IPR entries across every layer, each annotated with its layer's startAligner.
 * Used to show all IPRs from aligner 1 onwards (regardless of which layer they belong to).
 * @returns {{ entry: string, startAligner: number, endAligner: number }[]}
 */
const getAllIprEntriesWithMetadata = () => {
  if (!iprLayers) return [];
  const result = [];
  for (const { data } of getSortedLayers()) {
    if (data == null || !data.ipr || data.ipr.length === 0) continue;
    const effectiveStart = iprEffectiveStart(data.startAligner ?? 0);
    data.ipr.forEach((entry) => {
      result.push({ entry, startAligner: effectiveStart, endAligner: data.endAligner });
    });
  }
  return result;
};

/**
 * Get the start aligner number for the layer that applies to the given aligner.
 * Used for tooltip text: "IPR performed prior to stage {startAlignerNumber}".
 */
const getStartAlignerForAligner = (alignerNum) => {
  if (!iprLayers) return null;
  const current = normalizeAlignerNumber(alignerNum);
  if (current === null) return null;

  const layers = getSortedLayers();

  // IPR starts from aligner 1; no stage at aligner 0
  if (current === 0) return null;

  for (const { data } of layers) {
    if (data == null) continue;
    const effectiveStart = iprEffectiveStart(data.startAligner ?? 0);
    if (current >= effectiveStart && current <= data.endAligner) {
      return data.startAligner != null ? iprEffectiveStart(data.startAligner) : null;
    }
  }

  return null;
};

/**
 * Returns true if the FDI tooth number belongs to the given arch.
 * Upper arch: quadrant 1 (11–18) and quadrant 2 (21–28).
 * Lower arch: quadrant 3 (31–38) and quadrant 4 (41–48).
 * @param {number} fdi
 * @param {"upper"|"lower"} arch
 */
const isToothInArch = (fdi, arch) => {
  const n = parseInt(fdi, 10);
  if (Number.isNaN(n)) return false;
  return arch === "upper" ? (n >= 11 && n <= 28) : (n >= 31 && n <= 48);
};

/**
 * Get unique IPR start aligner numbers from all layers.
 * Used to show diamond markers on the slider for each IPR start.
 * @param {"upper"|"lower"|undefined} arch - If provided, only counts IPR entries for that arch.
 */
export const getIPRStartAlignerNumbers = (arch) => {
  if (!iprLayers) return [];
  const starts = new Set();

  for (const { data } of getSortedLayers()) {
    if (data == null || data.startAligner == null || !data.ipr || data.ipr.length === 0) continue;

    const hasArchIpr = arch
      ? data.ipr.some((entry) => {
          const toothStr = entry.includes(":") ? entry.split(":")[0] : entry.split(".")[0];
          return isToothInArch(toothStr, arch);
        })
      : true;

    if (hasArchIpr) {
      starts.add(iprEffectiveStart(data.startAligner));
    }
  }

  return [...starts].sort((a, b) => a - b);
};

/**
 * Get attachment layer start and end aligner numbers from all layers.
 * Markers are only shown when there is an actual change in attachments between
 * consecutive layers:
 *   - Up-arrow (start / addition): shown at layer N's startAligner when layer N
 *     introduces attachments that were NOT present in layer N-1.
 *   - Down-arrow (end / removal): driven by each layer's removedAttachments.
 *     If a layer has non-empty removedAttachments: show the marker at this layer's first aligner (startAligner).
 *     If the same layer also has new attachments added, only the add marker is shown (removal marker is hidden).
 *     If any attachments remain at the end of treatment, a removal marker is always shown at maxAligner
 *     (the last step of the greater arch), regardless of which arch is shorter.
 * For the first layer, "previous" is treated as no attachments.
 * @param {number} [maxAligner] - If provided, caps end aligners and marks end-of-treatment removal.
 * @param {"upper"|"lower"|undefined} [arch] - If provided, only considers attachments for that arch.
 */
export const getAttachmentLayerMarkers = (maxAligner, arch) => {
  if (!iprLayers) return { starts: [], ends: [] };
  const starts = new Set();
  const ends = new Set();

  const layers = getSortedLayers();
  if (layers.length === 0) return { starts: [], ends: [] };

  const filterByArch = (items) => {
    if (!arch || !Array.isArray(items)) return items || [];
    return items.filter((item) => isToothInArch(item, arch));
  };

  // Effective attachments per layer (arch-filtered): if layer has defined non-empty attachment use it,
  // otherwise carry forward from the previous layer minus this layer's removedAttachments (so that
  // add-then-remove-then-add again shows an add marker at the re-add layer).
  const effectiveAttachments = [];
  for (let i = 0; i < layers.length; i++) {
    const raw = filterByArch(layers[i].data.attachment);
    const hasOwn = Array.isArray(raw) && raw.length > 0;
    if (hasOwn) {
      effectiveAttachments.push(raw);
    } else {
      const prev = i > 0 ? effectiveAttachments[i - 1] : [];
      const removed = filterByArch(layers[i].data.removedAttachments);
      const removedSet = new Set(Array.isArray(removed) ? removed : []);
      effectiveAttachments.push(prev.filter((a) => !removedSet.has(a)));
    }
  }

  for (let i = 0; i < layers.length; i++) {
    const currentAttachments = new Set(effectiveAttachments[i]);
    const prevAttachments = i > 0
      ? new Set(effectiveAttachments[i - 1])
      : new Set();

    // Addition: current layer introduces at least one new attachment vs previous layer
    const hasNewAttachments = [...currentAttachments].some(
      (a) => !prevAttachments.has(a)
    );
    if (hasNewAttachments && layers[i].data.startAligner != null) {
      starts.add(layers[i].data.startAligner);
    }

    // Removal: use layer's removedAttachments (arch-filtered). Show at this layer's first aligner (startAligner).
    // If this layer also has new attachments, prefer add marker only.
    const removed = filterByArch(layers[i].data.removedAttachments);
    const hasRemoved = Array.isArray(removed) && removed.length > 0;
    if (hasRemoved && !hasNewAttachments && layers[i].data.startAligner != null) {
      const alignerToMark = layers[i].data.startAligner;
      const end = maxAligner != null ? Math.min(alignerToMark, maxAligner) : alignerToMark;
      ends.add(end);
    }
  }

  // If any attachments persist at the end of the last layer, they are always removed
  // at the final treatment step (maxAligner). This applies even when one arch is shorter
  // than the other — the removal marker is placed at the greater arch's last step.
  // When filtering by arch (upper or lower): also show end-of-treatment removal at maxAligner
  // if that arch had attachments in some layer but has none remaining at the end (so the
  // removal marker is visible at the end of the treatment timeline for that arch).
  const lastEffective = effectiveAttachments[effectiveAttachments.length - 1];
  const hasRemainingAtEnd = Array.isArray(lastEffective) && lastEffective.length > 0;
  const hadAttachmentsEarlier =
    (arch === "upper" || arch === "lower") &&
    !hasRemainingAtEnd &&
    effectiveAttachments.some((arr) => Array.isArray(arr) && arr.length > 0);
  if (hasRemainingAtEnd || hadAttachmentsEarlier) {
    const lastLayer = layers[layers.length - 1];
    const endOfTreatment = maxAligner != null
      ? maxAligner
      : (lastLayer.data.endAligner != null ? lastLayer.data.endAligner : null);
    if (endOfTreatment != null) {
      ends.add(endOfTreatment);
    }
  }

  return {
    starts: [...starts].sort((a, b) => a - b),
    ends: [...ends].sort((a, b) => a - b),
  };
};

/**
 * Convert IPR format to tooth pair and value.
 * Supports "tooth:value" (e.g. "21:0.3") and "tooth.value" (e.g. "15.04").
 * The key is one FDI; the IPR is between that tooth and its arch-neighbor.
 * Arch order: upper right 17→11 (-1), upper left 21→27 (+1), lower right 47→41 (-1), lower left 31→37 (+1).
 * Midline: 11↔21 and 31↔41 only for the central incisors (11 → 21, 41 → 31).
 */
const parseIPREntry = (entry) => {
  let toothStr;
  let value;
  if (entry.includes(":")) {
    [toothStr, value] = entry.split(":");
  } else if (entry.includes(".")) {
    const parts = entry.split(".");
    toothStr = parts[0];
    value = parts.slice(1).join(".");
    if (value.length >= 2 && !value.includes(".")) value = value.replace(/^0/, "0.") || value;
  } else {
    return { tooth1: null, tooth2: null, value: "" };
  }
  const toothNum = parseInt(toothStr, 10);
  if (Number.isNaN(toothNum)) return { tooth1: null, tooth2: null, value: value || "" };

  let nextTooth;
  if (toothNum === 11) {
    nextTooth = 21;
  } else if (toothNum === 41) {
    nextTooth = 31;
  } else if (toothNum >= 21 && toothNum <= 27) {
    nextTooth = toothNum + 1;
  } else if (toothNum >= 31 && toothNum <= 37) {
    nextTooth = toothNum + 1;
  } else if (toothNum >= 11 && toothNum <= 17) {
    nextTooth = toothNum - 1;
  } else if (toothNum >= 41 && toothNum <= 47) {
    nextTooth = toothNum - 1;
  } else {
    nextTooth = toothNum;
  }

  return {
    tooth1: toothStr,
    tooth2: nextTooth.toString(),
    value: value == null ? "" : String(value)
  };
};

/**
 * FDI neighbor along the arch as in IPR data (same rule as parseIPREntry tooth2).
 * Used to walk past missing teeth to the next present contact tooth.
 */
const getNominalArchNeighborFDI = (toothNum) => {
  if (toothNum === 11) return 21;
  if (toothNum === 41) return 31;
  if (toothNum >= 21 && toothNum <= 27) return toothNum + 1;
  if (toothNum >= 31 && toothNum <= 37) return toothNum + 1;
  if (toothNum >= 11 && toothNum <= 17) return toothNum - 1;
  if (toothNum >= 41 && toothNum <= 47) return toothNum - 1;
  return null;
};

/**
 * Resolve IPR anchor + nominal neighbor to FDI numbers that have meshes.
 * When the nominal neighbor is missing (e.g. IPR "15" with 14 missing), walks the arch
 * in the same direction until a non-missing tooth is found (e.g. 15–13).
 * @returns {{ tooth1: string, tooth2: string } | null}
 */
const resolveIPRPresentMeshPairFDI = (anchorStr, nominalNeighborStr) => {
  const missingSet = new Set(missingTeethFDI);
  const anchor = parseInt(anchorStr, 10);
  let neighbor = parseInt(nominalNeighborStr, 10);
  if (Number.isNaN(anchor) || Number.isNaN(neighbor)) {
    return null;
  }
  if (missingSet.has(anchor)) {
    return null;
  }

  const visited = new Set();
  while (missingSet.has(neighbor)) {
    if (visited.has(neighbor)) {
      return null;
    }
    visited.add(neighbor);
    const next = getNominalArchNeighborFDI(neighbor);
    if (next == null || next === neighbor) {
      return null;
    }
    neighbor = next;
  }
  if (neighbor === anchor) {
    return null;
  }
  return { tooth1: String(anchor), tooth2: String(neighbor) };
};

// IPR line rendering configuration
const IPR_CONFIG = {
  lineLength: 18,        // Length of visible line
  gapDistance: 3.4,        // Distance from teeth to line start
  lineWidth: 1,          // Line width in pixels
  lineColor: 0x000000,   // Black color for IPR lines
  downwardAdjustmentFactor: 1.3, // For anterior teeth vertical angle adjustment
  blendFactor: 0.6,      // Blend factor for anterior teeth horizontal angle (60% perpendicular, 40% base)
  verticalBiasFactor: 1.2, // Extra vertical tilt for upper/lower direction
  verticalAngleRatio: 0.4, // Target vertical ratio for all IPR lines
  horizontalStraightenFactor: 0.6, // Blend horizontal direction toward jaw center
  anteriorStraightenFactor: 0.8, // Stronger straightening for anterior teeth
};

// IPR line color palettes (vertex colors, RGB normalized 0–1)
const IPR_COLORS = {
  // Default: dark gradient (line not yet highlighted / already past its aligner)
  default: {
    startRGB: [0.149, 0.149, 0.149],  // 85% black at line start
    endRGB:   [0.051, 0.051, 0.051],  // 95% black at line end
  },
  // Highlighted: blue gradient matching the stripe-segment color (#6FC3DF) — shown exactly at startAligner
  highlighted: {
    startRGB: [0.549, 0.808, 0.902],  // lighter blue #8CCEE6 at line start
    endRGB:   [0.435, 0.765, 0.875],  // stripe-segment blue #6FC3DF at line end
  },
};

// Visibility thresholds
const VISIBILITY_THRESHOLDS = {
  farThreshold: 60,      // Distance threshold for far-left/far-right sections
  nearThreshold: 30,     // Distance threshold for left/right sections
  sideThreshold: 50,     // Distance threshold for side views
  verticalThreshold: 30, // Distance threshold for upper/lower views
  horizontalSectionBoundary: 30, // X coordinate boundary for horizontal sections
};

// ============================================================================
// DATA STORAGE
// ============================================================================

// Map to store tooth identifiers (FDI numbers) to their meshes
const toothMapping = new Map();

// Store all IPR lines with their metadata for visibility control
const iprLines = new Map(); // key: canvasId, value: array of { line, quadrant, horizontalSection, midpoint, endPoint, label }

// Expose iprLines to window for render loop access
window.iprLines = iprLines;

// Track the last known aligner number per canvas so updateIPRVisibility can respect aligner 0
const lastAlignerByCanvas = new Map();

// ============================================================================
// TOOTH IDENTIFICATION & MAPPING
// ============================================================================

// Missing teeth FDI numbers (e.g. [28, 38]). Set via setMissingTeethFDI() when case data is loaded.
let missingTeethFDI = [];

/**
 * Set the list of missing tooth FDI numbers. Used so index-to-FDI conversion
 * maps file indices to the correct FDI (e.g. if [26] is missing, index 13 may map to 27).
 * @param {number[]} fdiList - Array of FDI numbers, e.g. [28] or [26, 36]
 */
export const setMissingTeethFDI = (fdiList) => {
  missingTeethFDI = Array.isArray(fdiList) ? fdiList.map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n)) : [];
};

/**
 * Ordered FDI numbers for upper jaw (14 teeth): right 17-11, then left 21-27.
 * Models typically have 7 per side (no 3rd molars 18, 28).
 */
const UPPER_ORDERED_FDI = [17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27];

/**
 * Ordered FDI numbers for lower jaw (14 teeth): right 47-41, then left 31-37.
 */
const LOWER_ORDERED_FDI = [47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37];


/**
 * Convert file tooth index to FDI number.
 * File indices are full-arch positions 01-14 (gaps exist for missing teeth — those files simply
 * aren't present in the zip). Upper: 01->17..07->11, 08->21..14->27.
 * Lower: 01->47..07->41, 08->31..14->37.
 * @param {number|string} index - File tooth index (1-based, 1–14)
 * @param {string} jaw - "UPPER" or "LOWER"
 * @returns {number|null} FDI number or null if index out of range
 */
const convertIndexToFDI = (index, jaw) => {
  const idx = parseInt(index, 10);
  if (Number.isNaN(idx) || idx < 1) return null;

  const orderedFDI = jaw === "UPPER" ? UPPER_ORDERED_FDI : jaw === "LOWER" ? LOWER_ORDERED_FDI : null;
  if (!orderedFDI || idx > orderedFDI.length) return null;

  return orderedFDI[idx - 1];
};

/**
 * Extract tooth index from filename
 * Files use indices 01-14, not FDI numbers
 */
const extractToothIndex = (filename) => {
  // Extract the last part of the path (filename only)
  const parts = filename.split('/');
  const filenameOnly = parts[parts.length - 1];

  // Remove file extension
  const nameWithoutExt = filenameOnly.replace(/\.(ply|corto|ctm)$/i, '');

  // Look for patterns with 01-14 indices
  const patterns = [
    /tooth[_-]?(\d{2})/i,           // tooth_01, tooth-01, tooth01
    /(\d{2})[_-]?tooth/i,           // 01_tooth, 01-tooth, 01tooth
    /[_-](\d{2})[_-]/,              // _01_ or -01-
    /\/(\d{2})\//,                   // /01/ in path
    /^(\d{2})$/,                     // just the number: 01
    /[_-](\d{2})$/,                  // ending with _01 or -01
    /^(\d{2})[_-]/,                  // starting with 01_ or 01-
  ];

  for (const pattern of patterns) {
    const match = nameWithoutExt.match(pattern);
    if (match && match[1]) {
      const index = match[1];
      const num = parseInt(index);
      // Validate it's a valid index (01-14)
      if (num >= 1 && num <= 14) {
        return index;
      }
    }
  }

  return null;
};

/**
 * Store tooth mapping when loading models
 */
export const registerTooth = (filename, mesh, geometry, jaw, alignerNum, index) => {
  const toothIndex = extractToothIndex(filename);
  if (toothIndex) {
    // Convert file index to FDI number
    const fdiNumber = convertIndexToFDI(toothIndex, jaw);

    if (fdiNumber) {
      const key = `${jaw}_${alignerNum}_${fdiNumber}`;
      toothMapping.set(key, {
        mesh,
        geometry,
        jaw,
        alignerNum,
        toothNumber: fdiNumber.toString(), // Store as FDI number
        fileIndex: toothIndex, // Store original file index
        index,
        filename,
      });
    }
  }
};

/**
 * Find tooth mesh by FDI number
 * @param {string} canvasId - Canvas ID
 * @param {string} toothNumber - FDI tooth number
 * @param {string} jaw - Jaw type (UPPER/LOWER)
 * @param {string} alignerNum - Active aligner number (e.g., "00", "01", "02")
 */
const findToothMesh = (canvasId, toothNumber, jaw = null, alignerNum = null) => {
  // Try to find in mapping first - this should be the primary method
  for (const [key, value] of toothMapping.entries()) {
    if (value.toothNumber === toothNumber.toString()) {
      const { jaw: storedJaw, mesh, geometry, alignerNum: storedAlignerNum, toothNumber: fdiNumber } = value;
      
      // Filter by jaw if provided
      if (jaw && storedJaw !== jaw.toUpperCase()) {
        continue;
      }
      
      // Filter by aligner number if provided (prioritize active aligner)
      if (alignerNum !== null && storedAlignerNum !== alignerNum) {
        continue;
      }

      return {
        mesh,
        geometry,
        fdiNumber: fdiNumber ? parseInt(fdiNumber) : null,
      };
    }
  }

  // If alignerNum was specified but not found, try without aligner filter as fallback
  if (alignerNum !== null) {
    return findToothMesh(canvasId, toothNumber, jaw, null);
  }

  return null;
};

/**
 * Resolve IPR FDIs and locate meshes. If the clinical contact tooth has no entry in
 * toothMapping (file index / export mismatch), walk along the arch in the same direction
 * as nominal IPR until a non-missing tooth with a mesh is found.
 */
const findIPRMeshPairForCanvas = (canvasId, tooth1Num, tooth2Num, jaw, alignerNum) => {
  const base = resolveIPRPresentMeshPairFDI(tooth1Num, tooth2Num);
  if (!base) return null;

  const tryMesh = (fdiStr) =>
    findToothMesh(canvasId, fdiStr, jaw, alignerNum) ||
    findToothMesh(canvasId, fdiStr, jaw, null);

  const missingSet = new Set(missingTeethFDI);
  const anchor = parseInt(base.tooth1, 10);

  let t1 = tryMesh(base.tooth1);
  let t2 = tryMesh(base.tooth2);

  if (t1 && !t2) {
    let cur = parseInt(base.tooth2, 10);
    const visited = new Set();
    while (true) {
      const next = getNominalArchNeighborFDI(cur);
      if (next == null || next === cur || visited.has(next)) break;
      visited.add(cur);
      cur = next;
      if (cur === anchor) break;
      if (missingSet.has(cur)) continue;
      const t = tryMesh(String(cur));
      if (t) {
        t2 = t;
        break;
      }
    }
  }

  if (!t1 || !t2) return null;

  return {
    t1,
    t2,
    /** Clinical contact FDIs (after missing-tooth resolution) — labels and stable keys */
    resolved: { tooth1: base.tooth1, tooth2: base.tooth2 },
  };
};

/**
 * Get all registered teeth for debugging
 */
export const getToothMapping = () => {
  return toothMapping;
};

/**
 * Test the FDI mapping - for debugging (exposed on window)
 */
export const testFDIMapping = () => {};

// ============================================================================
// GEOMETRY CALCULATIONS
// ============================================================================

/**
 * Get the center point of a tooth mesh in world coordinates
 */
const getToothCenter = (mesh, geometry) => {
  if (!geometry || !mesh) return null;

  // Compute bounding box in local space
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;

  if (!box) return null;

  // Get center of bounding box in local space
  const localCenter = new THREE.Vector3();
  box.getCenter(localCenter);

  // Transform to world coordinates using the mesh's transform
  const worldCenter = localCenter.clone();
  mesh.localToWorld(worldCenter);

  return worldCenter;
};

/**
 * Get the center of the contact face (the face of bounding box closest to the other tooth)
 * This finds which face of the box is facing the other tooth and returns its center point
 */
const getContactFaceCenter = (mesh, geometry, otherCenter) => {
  if (!geometry || !mesh) return null;

  // Compute bounding box in local space
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;

  if (!box) return null;

  // Get the center of this tooth in local space
  const localCenter = new THREE.Vector3();
  box.getCenter(localCenter);

  // Transform to world coordinates
  const worldCenter = localCenter.clone();
  mesh.localToWorld(worldCenter);

  // Calculate direction from this tooth to the other tooth
  const directionToOther = new THREE.Vector3();
  directionToOther.subVectors(otherCenter, worldCenter);

  // Transform direction to local space to work with local bounding box
  const localDirection = directionToOther.clone();
  const meshWorldQuaternionInverse = mesh.quaternion.clone().invert();
  localDirection.applyQuaternion(meshWorldQuaternionInverse);
  localDirection.normalize();

  // Find which face of the bounding box is most aligned with the direction
  // The 6 faces are: +X, -X, +Y, -Y, +Z, -Z
  const faces = [
    { normal: new THREE.Vector3(1, 0, 0), point: new THREE.Vector3(box.max.x, localCenter.y, localCenter.z) },  // Right face (+X)
    { normal: new THREE.Vector3(-1, 0, 0), point: new THREE.Vector3(box.min.x, localCenter.y, localCenter.z) }, // Left face (-X)
    { normal: new THREE.Vector3(0, 1, 0), point: new THREE.Vector3(localCenter.x, box.max.y, localCenter.z) },  // Front face (+Y)
    { normal: new THREE.Vector3(0, -1, 0), point: new THREE.Vector3(localCenter.x, box.min.y, localCenter.z) }, // Back face (-Y)
    { normal: new THREE.Vector3(0, 0, 1), point: new THREE.Vector3(localCenter.x, localCenter.y, box.max.z) },  // Top face (+Z)
    { normal: new THREE.Vector3(0, 0, -1), point: new THREE.Vector3(localCenter.x, localCenter.y, box.min.z) }, // Bottom face (-Z)
  ];

  // Find the face with normal most aligned with direction to other tooth
  let bestFace = faces[0];
  let maxDot = -Infinity;

  faces.forEach(face => {
    const dot = face.normal.dot(localDirection);
    if (dot > maxDot) {
      maxDot = dot;
      bestFace = face;
    }
  });

  // Transform the face center point to world coordinates
  const contactFaceWorld = bestFace.point.clone();
  mesh.localToWorld(contactFaceWorld);

  return contactFaceWorld;
};

/**
 * Calculate midpoint between two teeth at their contact surfaces
 */
const getMidpointBetweenTeeth = (scene, tooth1, tooth2) => {
  // Get the centers of both teeth first
  const center1 = getToothCenter(tooth1.mesh, tooth1.geometry);
  const center2 = getToothCenter(tooth2.mesh, tooth2.geometry);

  if (!center1 || !center2) {
    return null;
  }

  // Get the contact face centers (the center of the faces closest to each other)
  const contactFace1 = getContactFaceCenter(tooth1.mesh, tooth1.geometry, center2);
  const contactFace2 = getContactFaceCenter(tooth2.mesh, tooth2.geometry, center1);

  if (!contactFace1 || !contactFace2) {
    return null;
  }

  // Calculate midpoint between the contact face centers
  const midpoint = new THREE.Vector3();
  midpoint.addVectors(contactFace1, contactFace2);
  midpoint.multiplyScalar(0.5);

  return { midpoint, center1, center2 };
};

// ============================================================================
// DIRECTION CALCULATION
// ============================================================================

/**
 * Check if a tooth is anterior (incisor/canine) or posterior (premolar/molar)
 * Based on FDI numbering: 11-13, 21-23, 31-33, 41-43 are anterior
 */
const isAnteriorTooth = (fdiNumber) => {
  const num = parseInt(fdiNumber);
  // Anterior teeth: 11-13 (upper right), 21-23 (upper left), 31-33 (lower left), 41-43 (lower right)
  return (num >= 11 && num <= 13) || (num >= 21 && num <= 23) ||
    (num >= 31 && num <= 33) || (num >= 41 && num <= 43);
};

/**
 * Check if an IPR line is for posterior (back) teeth
 * Returns true if BOTH teeth in the IPR pair are posterior teeth
 * Posterior teeth: 14-18, 24-28, 34-38, 44-48 (premolars and molars)
 */
const isPosteriorIPR = (tooth1FDI, tooth2FDI) => {
  if (!tooth1FDI || !tooth2FDI) return false;
  const num1 = parseInt(tooth1FDI);
  const num2 = parseInt(tooth2FDI);
  
  // Check if both teeth are posterior (premolars and molars)
  const isPosterior1 = (num1 >= 14 && num1 <= 18) || (num1 >= 24 && num1 <= 28) ||
    (num1 >= 34 && num1 <= 38) || (num1 >= 44 && num1 <= 48);
  const isPosterior2 = (num2 >= 14 && num2 <= 18) || (num2 >= 24 && num2 <= 28) ||
    (num2 >= 34 && num2 <= 38) || (num2 >= 44 && num2 <= 48);
  
  return isPosterior1 && isPosterior2;
};

/**
 * Calculate direction for anterior teeth (front teeth need adjusted angle)
 */
const calculateAnteriorDirection = (midpoint, center1, center2, jaw) => {
  // Calculate vector from center1 to center2 (direction along the arch)
  const archDirection = new THREE.Vector3();
  archDirection.subVectors(center2, center1);
  archDirection.normalize();

  // Project arch direction onto X/Y plane (remove Z component)
  const archDirectionXY = new THREE.Vector3(archDirection.x, archDirection.y, 0);
  archDirectionXY.normalize();

  // Get the base outward direction (same as posterior)
  const baseDirection = new THREE.Vector3();
  baseDirection.copy(midpoint);
  baseDirection.z = 5; // Keep Z component same as posterior
  baseDirection.normalize();

  // Calculate perpendicular direction in X/Y plane using cross product with Z-axis
  // Perpendicular = archDirectionXY × (0, 0, 1) gives us the perpendicular in horizontal plane
  const zAxis = new THREE.Vector3(0, 0, 1);
  const perpendicularXY = new THREE.Vector3();
  perpendicularXY.crossVectors(archDirectionXY, zAxis);
  perpendicularXY.normalize();

  // Determine which direction to use (toward or away from origin)
  // Use the one that points more outward (has positive dot product with midpoint direction)
  const midpointXY = new THREE.Vector3(midpoint.x, midpoint.y, 0);
  midpointXY.normalize();

  const dot1 = perpendicularXY.dot(midpointXY);
  const dot2 = perpendicularXY.clone().multiplyScalar(-1).dot(midpointXY);

  // Use the direction that points more outward
  if (dot2 > dot1) {
    perpendicularXY.multiplyScalar(-1);
  }

  // For front teeth, blend the perpendicular direction with the base direction
  // This gives a more gradual adjustment rather than completely perpendicular
  const baseXY = new THREE.Vector3(baseDirection.x, baseDirection.y, 0);
  baseXY.normalize();

  const blendedXY = new THREE.Vector3();
  blendedXY.addVectors(
    perpendicularXY.clone().multiplyScalar(IPR_CONFIG.blendFactor),
    baseXY.clone().multiplyScalar(1 - IPR_CONFIG.blendFactor)
  );
  blendedXY.normalize();

  // Create final direction: blended X/Y, adjust vertical angle to point more downward
  // For front teeth, we want the lines to point more downward (toward gum line)
  const labialDirection = new THREE.Vector3();
  labialDirection.x = blendedXY.x;
  // Decrease Y component to make lines point more downward (assuming positive Y is upward)
  labialDirection.y = baseDirection.y * (1 - (IPR_CONFIG.downwardAdjustmentFactor - 1));
  labialDirection.z = baseDirection.z; // Keep Z component
  // Apply jaw-specific vertical bias (upper up, lower down)
  if (jaw === "UPPER" || jaw === "LOWER") {
    const bias = 1 + IPR_CONFIG.verticalBiasFactor;
    const magnitude = Math.abs(labialDirection.y) * bias;
    labialDirection.y = jaw === "UPPER" ? magnitude : -magnitude;
  }

  labialDirection.normalize();

  return labialDirection;
};

/**
 * Calculate direction for posterior teeth (back teeth use standard approach)
 */
const calculatePosteriorDirection = (midpoint, jaw) => {
  const labialDirection = new THREE.Vector3();
  labialDirection.copy(midpoint);
  labialDirection.z = 5; // Zero out Z to stay in horizontal plane
  // Apply a consistent vertical tilt and jaw-specific bias
  labialDirection.y = labialDirection.y * (1 - (IPR_CONFIG.downwardAdjustmentFactor - 1));
  if (jaw === "UPPER" || jaw === "LOWER") {
    const bias = 1 + IPR_CONFIG.verticalBiasFactor;
    const magnitude = Math.abs(labialDirection.y) * bias;
    labialDirection.y = jaw === "UPPER" ? magnitude : -magnitude;
  }
  labialDirection.normalize();
  return labialDirection;
};

/**
 * Determine direction to draw IPR line (towards outside/labial)
 * The line should point outward from between the teeth toward the lips/cheeks
 * Front teeth need a different angle than back teeth due to arch curvature
 */
const getIPRDirection = (jaw, center1, center2, tooth1FDI = null, tooth2FDI = null) => {
  // Calculate midpoint between teeth
  const midpoint = new THREE.Vector3();
  midpoint.addVectors(center1, center2);
  midpoint.multiplyScalar(0.5);

  // Check if these are anterior teeth
  const isAnterior = (tooth1FDI && isAnteriorTooth(tooth1FDI)) ||
    (tooth2FDI && isAnteriorTooth(tooth2FDI));

  // Use different calculation for anterior vs posterior teeth
  if (isAnterior) {
    return calculateAnteriorDirection(midpoint, center1, center2, jaw);
  } else {
    return calculatePosteriorDirection(midpoint, jaw);
  }
};

// ============================================================================
// VISIBILITY MANAGEMENT
// ============================================================================

/**
 * Determine which horizontal section an IPR line is in (4 horizontal divisions)
 * Returns 'far-left', 'left', 'right', or 'far-right'.
 * In portrait, scene is rotated 90° so left/right on screen corresponds to world Y; use midpoint.y.
 */
const getIPRHorizontalSection = (midpoint) => {
  const horiz = isPortrait ? midpoint.y : midpoint.x;
  const boundary = VISIBILITY_THRESHOLDS.horizontalSectionBoundary;

  if (horiz < -boundary) return 'far-left';
  if (horiz < 0) return 'left';
  if (horiz < boundary) return 'right';
  return 'far-right';
};

/**
 * Determine which quadrant an IPR line is in
 * Returns 'upper-right', 'upper-left', 'lower-right', or 'lower-left'
 * Based on X (left/right) and Y or Z (upper/lower). In portrait, left/right follows world Y.
 */
const getIPRQuadrant = (midpoint, jaw) => {
  const isRight = (isPortrait ? midpoint.y : midpoint.x) >= 0;
  const isUpper = jaw === "UPPER";

  if (isUpper && isRight) return 'upper-right';
  if (isUpper && !isRight) return 'upper-left';
  if (!isUpper && isRight) return 'lower-right';
  if (!isUpper && !isRight) return 'lower-left';

  // Fallback based on Y coordinate if jaw is not provided
  const isUpperByY = midpoint.y >= 0 || midpoint.z >= 0;
  if (isRight && isUpperByY) return 'upper-right';
  if (!isRight && isUpperByY) return 'upper-left';
  if (isRight && !isUpperByY) return 'lower-right';
  return 'lower-left';
};

/**
 * Determine which horizontal section(s) the camera is viewing (4 horizontal divisions)
 * Returns an array: ['far-left'], ['left'], ['right'], ['far-right'], or combinations.
 * In portrait, scene is rotated 90° so horizontal on screen = world Y; use camera Y.
 */
const getCameraViewHorizontalSections = (camera) => {
  const cameraPos = camera.position;
  const cameraHoriz = isPortrait ? cameraPos.y : cameraPos.x;
  const { farThreshold, nearThreshold } = VISIBILITY_THRESHOLDS;

  // Camera position logic:
  // If camera horiz is positive (camera on left), it's viewing the RIGHT side
  // If camera horiz is negative (camera on right), it's viewing the LEFT side

  if (Math.abs(cameraHoriz) > farThreshold) {
    // Camera is far to the side - viewing the opposite far section and adjacent
    if (cameraHoriz > farThreshold) {
      // Camera far left, viewing far-right and right sections
      return ['right', 'far-right'];
    } else {
      // Camera far right, viewing far-left and left sections
      return ['far-left', 'left'];
    }
  } else if (Math.abs(cameraHoriz) > nearThreshold) {
    // Camera is moderately to the side - viewing the opposite section and adjacent
    if (cameraHoriz > nearThreshold) {
      // Camera left, viewing right and far-right sections
      return ['right', 'far-right'];
    } else {
      // Camera right, viewing left and far-left sections
      return ['far-left', 'left'];
    }
  } else {
    // Camera is near center - viewing multiple sections
    if (cameraHoriz > 15) {
      // Slightly left of center, viewing right and far-right
      return ['right', 'far-right'];
    } else if (cameraHoriz < -15) {
      // Slightly right of center, viewing left and far-left
      return ['far-left', 'left'];
    } else {
      // Near center - show all sections
      return ['far-left', 'left', 'right', 'far-right'];
    }
  }
};

/**
 * Determine which quadrant(s) of the mouth the camera is viewing
 * Returns an array of quadrants: ['upper-right'], ['upper-left'], etc.
 * Can return multiple quadrants if viewing from front/rear.
 * In portrait, horizontal on screen = world Y; use camera Y for left/right.
 */
const getCameraViewQuadrants = (camera) => {
  const cameraPos = camera.position;
  const cameraHoriz = isPortrait ? cameraPos.y : cameraPos.x;
  const { sideThreshold } = VISIBILITY_THRESHOLDS;

  // Determine horizontal view (left/right)
  let horizontalView = null;
  if (Math.abs(cameraHoriz) > sideThreshold) {
    // Camera is on the side - return which side is being VIEWED
    // Positive horiz (camera on left) = viewing right side
    // Negative horiz (camera on right) = viewing left side
    horizontalView = cameraHoriz > 0 ? 'right' : 'left';
  }

  // If only horizontal, return both upper and lower of that side
  if (horizontalView) {
    return [`upper-${horizontalView}`, `lower-${horizontalView}`];
  }

  // Front, rear, or other views - show all quadrants
  return ['upper-right', 'upper-left', 'lower-right', 'lower-left'];
};

/**
 * Determine if the camera is viewing from the front or back of the jaw
 * Front view: Camera Z is high (looking at labial/buccal surface from outside)
 * Rotated/side view: Camera Z drops as camera rotates around the model
 * Returns: 'front' or 'neutral'
 */
const getCameraFrontBackView = (camera) => {
  const cameraPos = camera.position;
  const cameraZ = cameraPos.z;
  
  // Threshold for determining front view
  // Default front view has Z ~112, rotated views have Z ~25-60
  // Use 100 as threshold so posterior IPR lines appear with minimal rotation
  const frontThreshold = 110;
  
  if (cameraZ > frontThreshold) {
    return 'front';
  }
  return 'neutral';
};

/**
 * Update IPR line visibility based on camera position
 */
export const updateIPRVisibility = (canvasId) => {
  const canvasData = canvasManager.get(canvasId);
  if (!canvasData) return;

  // Aligner 0 is the passive tray — keep all lines hidden regardless of camera changes
  if (lastAlignerByCanvas.get(canvasId) === 0) {
    const lines = iprLines.get(canvasId) || [];
    lines.forEach(({ line, label }) => {
      line.visible = false;
      if (label) {
        label.dataset.visible = "false";
        label.style.opacity = "0";
      }
    });
    return;
  }

  // Diff view canvas: never show IPR
  if (canvasId === "diff-canvas") {
    const lines = iprLines.get(canvasId) || [];
    lines.forEach(({ line, label }) => {
      line.visible = false;
      if (label) {
        label.dataset.visible = "false";
        label.style.opacity = "0";
      }
    });
    return;
  }

  const { camera, renderer, visibilityMode } = canvasData;
  if (!camera || !renderer) return;

  // If IPR is globally disabled, hide all lines for this canvas and return
  if (!iprEnabled) {
    const linesToHide = iprLines.get(canvasId) || [];
    linesToHide.forEach(({ line, label }) => {
      line.visible = false;
      if (label) {
        label.dataset.visible = "false";
        label.style.opacity = "0";
      }
    });
    return;
  }

  const viewQuadrants = getCameraViewQuadrants(camera);
  const viewHorizontalSections = getCameraViewHorizontalSections(camera);
  const frontBackView = getCameraFrontBackView(camera);
  const lines = iprLines.get(canvasId) || [];

  lines.forEach(({ line, quadrant, horizontalSection, endPoint, label, isPosterior }) => {
    // Show lines only if both their quadrant AND horizontal section are in the view
    const quadrantVisible = viewQuadrants.includes(quadrant);
    const horizontalVisible = viewHorizontalSections.includes(horizontalSection);
    const isUpperQuadrant = quadrant.startsWith("upper");
    const isLowerQuadrant = quadrant.startsWith("lower");
    let jawVisible = true;

    if (visibilityMode === VISIBILITY_MODES.UPPER) {
      jawVisible = isUpperQuadrant;
    } else if (visibilityMode === VISIBILITY_MODES.LOWER) {
      jawVisible = isLowerQuadrant;
    }

    // Front/back visibility logic:
    // When viewing from front, hide posterior (back) IPR lines
    // When viewing from back, show posterior IPR lines
    // When neutral, show all
    let frontBackVisible = true;
    if (frontBackView === 'front' && isPosterior) {
      // Camera is at front of jaw, hide back teeth IPR lines
      frontBackVisible = false;
    }
    // When frontBackView is 'back' or 'neutral', show all IPR lines
    // IPR is visible regardless of gums visibility so both can be on at once
    const isVisible = jawVisible && quadrantVisible && horizontalVisible && frontBackVisible;

    line.visible = isVisible;

    // Update label visibility and position
    if (label) {
      label.dataset.visible = isVisible.toString();
      if (isVisible) {
        if (isMobilePortrait()) {
          positionMobileIPRLabelFromLineEnd(label, line, camera, renderer, endPoint);
        } else if (endPoint) {
          updateLabelPosition(label, endPoint, camera, renderer);
        }
      } else {
        label.style.opacity = '0';
      }
    }
  });
};

// ============================================================================
// IPR GLOBAL TOGGLE
// ============================================================================

// Global flag to track if IPR visualization is enabled (visible by default)
let iprEnabled = true;

/**
 * Toggle IPR visualization on/off globally (one toggle per click, applied to all canvases)
 */
export const toggleIPR = (canvasId) => {
  iprEnabled = !iprEnabled;

  const allCanvasIds = canvasManager.getAll().map((c) => c.id);

  if (!iprEnabled) {
    // Hide all IPR lines and labels on every canvas
    allCanvasIds.forEach((id) => {
      const lines = iprLines.get(id) || [];
      lines.forEach(({ line, label }) => {
        line.visible = false;
        if (label) {
          label.dataset.visible = "false";
          label.style.opacity = "0";
        }
      });
    });
  } else {
    // Re-enable IPR and update visibility based on camera for every canvas
    allCanvasIds.forEach((id) => updateIPRVisibility(id));
  }

  return iprEnabled;
};

/**
 * Check if IPR visualization is currently enabled
 */
export const isIPREnabled = () => iprEnabled;

/**
 * Clean up IPR visualization for a canvas (remove lines from scene and labels from DOM)
 */
export const cleanupIPRVisualization = (canvasId) => {
  const lines = iprLines.get(canvasId);
  if (!lines) return;
  
  const canvasData = canvasManager.get(canvasId);
  const scene = canvasData?.scene;
  
  lines.forEach(({ line, label }) => {
    // Remove line from scene
    if (scene && line) {
      scene.remove(line);
      // Dispose of geometry and material
      if (line.geometry) line.geometry.dispose();
      if (line.material) line.material.dispose();
      // Call cleanup function if exists
      if (line.userData?.cleanup) line.userData.cleanup();
    }
    
    // Remove label from DOM
    if (label && label.parentNode) {
      label.parentNode.removeChild(label);
    }
  });
  
  // Clear the lines array for this canvas
  iprLines.delete(canvasId);
};

// ============================================================================
// LABEL MANAGEMENT
// ============================================================================

const IPR_LABELS_OVERLAY_ID = 'ipr-labels-overlay';

/** True when in portrait and narrow viewport (mobile). Exported for render-loop use. */
export const isMobilePortrait = () => isPortrait && window.innerWidth <= 500;

/**
 * Get world position of the line's end vertex (where the IPR line actually ends after transform).
 * LineSegmentsGeometry uses instanceStart/instanceEnd, not position — we read instanceEnd.
 */
const getIPRLineEndWorld = (line) => {
  const endAttr = line?.geometry?.attributes?.instanceEnd;
  if (!endAttr || endAttr.count < 1) return null;
  const localEnd = new THREE.Vector3();
  localEnd.fromBufferAttribute(endAttr, 0);
  line.updateMatrixWorld(true);
  return localEnd.applyMatrix4(line.matrixWorld);
};

const syncOverlayToCanvas = (overlay, canvas) => {
  if (!overlay || !canvas) return;
  const computed = window.getComputedStyle(canvas);
  overlay.style.position = 'absolute';
  overlay.style.top = computed.top;
  overlay.style.left = computed.left;
  overlay.style.width = computed.width;
  overlay.style.height = computed.height;
  overlay.style.transform = computed.transform;
  overlay.style.transformOrigin = computed.transformOrigin;
  overlay.style.pointerEvents = 'none';
  overlay.style.zIndex = '9999';
};

/**
 * Get or create a label overlay that matches the canvas transform.
 * We keep labels in the same coordinate system as the canvas so projection math stays simple.
 */
const getIPRLabelsOverlayForCanvas = (canvas) => {
  if (!canvas) return null;
  const overlayId = `${IPR_LABELS_OVERLAY_ID}-${canvas.id || 'default'}`;
  let el = document.getElementById(overlayId);
  if (!el) {
    el = document.createElement('div');
    el.id = overlayId;
    el.style.cssText = 'position:absolute;pointer-events:none;z-index:9999;';
    (canvas.parentElement || document.body).appendChild(el);
  }
  syncOverlayToCanvas(el, canvas);
  return el;
};

/**
 * Position an IPR label for mobile: use the line's end-point coordinates, project to buffer,
 * adjust for canvas 90° CW rotation, then set viewport position. Call after lines are loaded
 * (e.g. in visibility update or render loop) so labels align with the rendered line ends.
 */
/**
 * @param {HTMLElement} label
 * @param {THREE.LineSegments2} line
 * @param {THREE.Camera} camera
 * @param {THREE.WebGLRenderer} renderer
 * @param {THREE.Vector3} [worldEndOverride] - if provided, use this world position for projection (e.g. stored endPoint)
 */
/**
 * @param {boolean} [enableTransition] - If true, position animates (aligner change). If false, label snaps to line end (camera/view change).
 */
export const positionMobileIPRLabelFromLineEnd = (label, line, camera, renderer, worldEndOverride, enableTransition = false) => {
  if (!label || !camera || !renderer) return;
  const worldEnd = worldEndOverride || (line ? getIPRLineEndWorld(line) : null);
  if (!worldEnd) return;

  label.style.transition = enableTransition
    ? "left 0.25s ease-out, top 0.25s ease-out, opacity 0.2s"
    : "opacity 0.2s";

  const canvas = renderer.domElement;
  const overlay = getIPRLabelsOverlayForCanvas(canvas);
  if (overlay && label.parentElement !== overlay) {
    overlay.appendChild(label);
  }

  const vector = worldEnd.clone().project(camera);
  const cw = canvas.clientWidth;
  const ch = canvas.clientHeight;
  const canvasLocalX = (vector.x * 0.5 + 0.5) * cw;
  const canvasLocalY = (vector.y * -0.5 + 0.5) * ch;

  label.style.position = "absolute";
  label.style.left = `${canvasLocalX}px`;
  label.style.top = `${canvasLocalY}px`;
  // Counter-rotate so label reads upright (overlay/canvas are rotated 90deg CW in portrait)
  label.style.transform = "translate(-50%, -50%) rotate(-90deg)";
  const shouldBeVisible = label.dataset.visible === "true";
  label.style.opacity = shouldBeVisible ? "1" : "0";
};

/**
 * Get or create a viewport-fixed overlay for IPR labels in portrait.
 * Appended to document.documentElement so position:fixed is viewport-relative
 * and labels are not affected by body's transform.
 */
const getIPRLabelsOverlay = () => {
  let el = document.getElementById(IPR_LABELS_OVERLAY_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = IPR_LABELS_OVERLAY_ID;
    el.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;';
    document.documentElement.appendChild(el);
  }
  return el;
};

/**
 * Create a diamond-shaped label element for IPR value.
 * @param {string} value - IPR value (e.g. "0.6")
 * @param {string} id - Unique label id (e.g. "canvasId-toothPair")
 * @param {string} canvasId - Canvas id for overlay
 * @param {{ tooth1: string, tooth2: string, value: string, startAlignerNumber: number|null }|undefined} tooltipData - Data for hover tooltip
 */
const createIPRLabel = (value, id, canvasId, tooltipData) => {
  const label = document.createElement('span');
  label.className = 'ipr-label';
  label.id = `ipr-label-${id}`;

  const isMobile = isPortrait;
  const size = isMobile ? '18px' : '26px';
  const fontSize = isMobile ? '7px' : '9px';

  // Outer label: positioning only, no rotation — so tooltip "bottom" is screen bottom
  label.style.cssText = `
    position: absolute;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    width: ${size};
    height: ${size};
    pointer-events: auto;
    z-index: 10;
    transform: translate(-50%, -50%);
    transform-origin: center;
    opacity: 0;
    transition: opacity 0.2s;
  `;

  // Inner diamond: visual only (rotated) so tooltip stays in unrotated space
  const diamond = document.createElement('span');
  diamond.style.cssText = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${size};
    height: ${size};
    background: #ffffff;
    color: #000000;
    font-size: ${fontSize};
    font-weight: normal;
    font-family: sans-serif;
    white-space: nowrap;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transform: rotate(45deg);
    transform-origin: center;
  `;
  const labelText = document.createElement('span');
  labelText.textContent = value;
  labelText.style.display = 'inline-block';
  // -45deg cancels diamond's 45deg so text reads upright (label has counter-rotation in portrait)
  const textRotation = '-45deg';
  labelText.style.transform = `rotate(${textRotation})`;
  diamond.appendChild(labelText);
  label.appendChild(diamond);

  // Set initial visibility state - labels should be visible by default until visibility logic determines otherwise
  label.dataset.visible = 'true';

  // Tooltip on hover: "Between []-[], {content} mm, IPR performed prior to stage {startAlignerNumber}"
  if (tooltipData) {
    const stageNum = tooltipData.startAlignerNumber != null ? tooltipData.startAlignerNumber : '?';
    const tooltipText = `Between ${tooltipData.tooth1}-${tooltipData.tooth2}, ${tooltipData.value} mm IPR performed prior to stage ${stageNum}.`;
    const tooltip = document.createElement('span');
    tooltip.className = 'ipr-label-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.style.cssText = `
      position: absolute;
      left: 50%;
      top: 100%;
      transform: translateX(-50%) translateY(18px);
      padding: 8px 12px;
      background: #b6e9fa;
      color: #424242;
      font-size: 12px;
      font-weight: normal;
      font-family: sans-serif;
      white-space: nowrap;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.15s, visibility 0.15s;
      z-index: 20;
      pointer-events: none;
    `;
    // Caret (upward triangle) at top center of tooltip
    const caret = document.createElement('span');
    caret.style.cssText = `
      position: absolute;
      left: 50%;
      bottom: 100%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top: none;
      border-bottom-color: #b6e9fa;
      width: 0;
      height: 0;
    `;
    tooltip.appendChild(caret);
    tooltip.appendChild(document.createTextNode(tooltipText));

    label.appendChild(tooltip);

    label.addEventListener('mouseenter', () => {
      // Elevate this label's stacking context so its tooltip renders above all sibling labels
      label.style.zIndex = '1000';
      diamond.style.outline = '1px solid #e93435';
      diamond.style.outlineOffset = '1px';
      if (isMobilePortrait()) {
        // In portrait, label has rotate(-90deg). Place tooltip below the label in viewport coords.
        // Use rAF to ensure layout is settled before measuring.
        const portal = getIPRLabelsOverlay();
        const portraitTooltip = document.createElement('span');
        portraitTooltip.className = 'ipr-label-tooltip ipr-portrait-tooltip';
        portraitTooltip.setAttribute('role', 'tooltip');
        portraitTooltip.textContent = tooltipText;
        portraitTooltip.style.cssText = `
          position: fixed;
          padding: 4px 6px;
          background: #b6e9fa;
          color: #424242;
          font-size: 9px;
          font-family: sans-serif;
          white-space: nowrap;
          border-radius: 4px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
          z-index: 20;
          pointer-events: none;
          visibility: hidden;
        `;
        portal.appendChild(portraitTooltip);
        label._portraitTooltip = portraitTooltip;
        requestAnimationFrame(() => {
          const rect = label.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          // Label has rotate(-90deg). Its local "bottom" (+Y) maps to viewport "left" (-X).
          // So "below" the diamond (away from line) = offset (-28, 0) in viewport.
          const tooltipLeft = centerX - 28;
          const tooltipTop = centerY;
          portraitTooltip.style.left = `${tooltipLeft}px`;
          portraitTooltip.style.top = `${tooltipTop}px`;
          portraitTooltip.style.transform = 'translate(-50%, -50%) rotate(90deg)';
          portraitTooltip.style.transformOrigin = 'center center';
          portraitTooltip.style.visibility = 'visible';
        });
      } else {
        tooltip.style.visibility = 'visible';
        tooltip.style.opacity = '1';
      }
    });
    label.addEventListener('mouseleave', () => {
      label.style.zIndex = '10';
      tooltip.style.visibility = 'hidden';
      tooltip.style.opacity = '0';
      diamond.style.outline = '';
      diamond.style.outlineOffset = '';
      if (isMobilePortrait() && label._portraitTooltip) {
        label._portraitTooltip.remove();
        label._portraitTooltip = null;
      }
    });
  }

  // Append to overlay aligned with the canvas on mobile; otherwise use body
  let appendTarget;
  if (isPortrait && window.innerWidth <= 500 && canvasId) {
    const canvas = canvasManager.get(canvasId)?.renderer?.domElement;
    const overlay = getIPRLabelsOverlayForCanvas(canvas);
    appendTarget = overlay || document.body;
    appendTarget.appendChild(label);
  } else {
    appendTarget = document.body;
    appendTarget.appendChild(label);
  }

  return label;
};

/**
 * Update label position based on 3D world coordinates.
 * @param {boolean} [enableTransition] - If true, position change animates (e.g. on aligner change). If false, label snaps so it sticks to the line when camera/view changes.
 */
export const updateLabelPosition = (label, worldPosition, camera, renderer, enableTransition = false) => {
  if (!label || !worldPosition || !camera || !renderer) return;

  // When camera/view changes we want the label to snap to the line end (no transition). Only aligner changes use transition.
  label.style.transition = enableTransition
    ? "transform 0.25s ease-out, left 0.25s ease-out, top 0.25s ease-out, opacity 0.2s"
    : "opacity 0.2s";

  // Convert 3D world position to 2D screen coordinates
  const vector = worldPosition.clone();
  vector.project(camera);

  // Get canvas bounding rect - this gives visual screen position after all transforms
  const canvasRect = renderer.domElement.getBoundingClientRect();
  const isPortraitMode = isPortrait && window.innerWidth <= 500;

  let finalX, finalY;

  if (isPortraitMode) {
    // Portrait: labels live in a viewport overlay (document.documentElement), so use viewport
    // coords directly. Canvas is CSS-rotated 90deg CW: (dx,dy) -> (dy,-dx).
    const cw = renderer.domElement.clientWidth;
    const ch = renderer.domElement.clientHeight;

    const canvasLocalX = (vector.x * 0.5 + 0.5) * cw;
    const canvasLocalY = (vector.y * -0.5 + 0.5) * ch;

    const visualCenterX = canvasRect.left + canvasRect.width / 2;
    const visualCenterY = canvasRect.top + canvasRect.height / 2;
    const offsetX = canvasLocalX - cw / 2;
    const offsetY = canvasLocalY - ch / 2;
    const viewportX = visualCenterX + offsetY;
    const viewportY = visualCenterY - offsetX;

    finalX = viewportX;
    finalY = viewportY;
    label.style.position = "fixed";
  } else {
    finalX = (vector.x * 0.5 + 0.5) * renderer.domElement.clientWidth + canvasRect.left;
    finalY = (vector.y * -0.5 + 0.5) * renderer.domElement.clientHeight + canvasRect.top;
    label.style.position = "absolute";
  }

  // Position via translate; no transition when enableTransition is false so label sticks to line end
  label.style.left = isPortraitMode ? "0" : `${canvasRect.left}px`;
  label.style.top = isPortraitMode ? "0" : `${canvasRect.top}px`;
  const tx = isPortraitMode ? finalX : finalX - canvasRect.left;
  const ty = isPortraitMode ? finalY : finalY - canvasRect.top;
  // Outer label: translate only (diamond rotation is on inner element so tooltip stays below in screen space)
  label.style.transform = `translate(${tx}px, ${ty}px) translate(-50%, -50%)`;
  const shouldBeVisible = label.dataset.visible === "true";
  label.style.opacity = shouldBeVisible ? "1" : "0";
};

// ============================================================================
// LINE RENDERING
// ============================================================================

/**
 * Compute IPR line positions (scene-space for line, world for label) between two teeth.
 * Shared by drawIPRLine and updateIPRLineInPlace so positions can be updated without recreating.
 * @returns {{ lineStartPoint: THREE.Vector3, endPoint: THREE.Vector3, labelWorldEnd: THREE.Vector3, midpoint: THREE.Vector3, sceneMidpoint: THREE.Vector3 } | null}
 */
const computeIPRLinePositions = (scene, tooth1, tooth2, jaw) => {
  const result = getMidpointBetweenTeeth(scene, tooth1, tooth2);
  if (!result) return null;

  const { midpoint, center1, center2 } = result;

  const worldToScene = (v) => new THREE.Vector3(v.y, -v.x, v.z);

  const sceneMidpoint = isPortrait ? worldToScene(midpoint) : midpoint.clone();
  const sceneCenter1 = isPortrait ? worldToScene(center1) : center1.clone();
  const sceneCenter2 = isPortrait ? worldToScene(center2) : center2.clone();

  const tooth1FDI = tooth1.fdiNumber || null;
  const tooth2FDI = tooth2.fdiNumber || null;
  const isAnterior =
    (tooth1FDI && isAnteriorTooth(tooth1FDI)) ||
    (tooth2FDI && isAnteriorTooth(tooth2FDI));

  const direction = getIPRDirection(jaw, sceneCenter1, sceneCenter2, tooth1FDI, tooth2FDI);
  const directionNormalized = direction.clone().normalize();
  const adjustedDirection = directionNormalized.clone();
  const horizontalMagnitude = Math.sqrt(
    adjustedDirection.x * adjustedDirection.x +
      adjustedDirection.z * adjustedDirection.z
  );
  if (horizontalMagnitude > 0) {
    const targetY =
      jaw === "LOWER"
        ? -IPR_CONFIG.verticalAngleRatio
        : IPR_CONFIG.verticalAngleRatio;
    const targetHorizontal = Math.sqrt(1 - targetY * targetY);
    adjustedDirection.x =
      (adjustedDirection.x / horizontalMagnitude) * targetHorizontal;
    adjustedDirection.z =
      (adjustedDirection.z / horizontalMagnitude) * targetHorizontal;
    adjustedDirection.y = targetY;
    adjustedDirection.normalize();
  }
  const midpointHorizontal = new THREE.Vector3(sceneMidpoint.x, 0, sceneMidpoint.z);
  const midpointHorizontalMagnitude = midpointHorizontal.length();
  if (horizontalMagnitude > 0 && midpointHorizontalMagnitude > 0) {
    const currentHorizontal = new THREE.Vector3(
      adjustedDirection.x,
      0,
      adjustedDirection.z
    ).normalize();
    const targetHorizontal = midpointHorizontal.normalize();
    const blend = isAnterior
      ? IPR_CONFIG.anteriorStraightenFactor
      : IPR_CONFIG.horizontalStraightenFactor;
    const blendedHorizontal = currentHorizontal
      .clone()
      .multiplyScalar(1 - blend)
      .add(targetHorizontal.clone().multiplyScalar(blend))
      .normalize();
    const targetY =
      jaw === "LOWER"
        ? -IPR_CONFIG.verticalAngleRatio
        : IPR_CONFIG.verticalAngleRatio;
    const targetHorizontalMagnitude = Math.sqrt(1 - targetY * targetY);
    adjustedDirection.x = blendedHorizontal.x * targetHorizontalMagnitude;
    adjustedDirection.z = blendedHorizontal.z * targetHorizontalMagnitude;
    adjustedDirection.y = targetY;
    adjustedDirection.normalize();
  }

  const lineStartPoint = new THREE.Vector3();
  lineStartPoint.addVectors(sceneMidpoint, adjustedDirection.clone().multiplyScalar(IPR_CONFIG.gapDistance));

  const endPoint = new THREE.Vector3();
  endPoint.addVectors(lineStartPoint, adjustedDirection.clone().multiplyScalar(IPR_CONFIG.lineLength));

  const sceneToWorld = (v) => new THREE.Vector3(-v.y, v.x, v.z);
  const labelWorldEnd = isPortrait ? sceneToWorld(endPoint) : endPoint.clone();

  return { lineStartPoint, endPoint, labelWorldEnd, midpoint, sceneMidpoint };
};

/**
 * Draw IPR line between two teeth
 * @param {boolean} [isHighlighted] - If true, renders with blue gradient (IPR performed at current aligner)
 * @param {{ tooth1: string, tooth2: string }|null} [clinicalPair] - FDI pair for tooltip/keys when meshes
 *   use a different registered tooth (e.g. line anchored on 12’s mesh for 15–13 IPR)
 */
const drawIPRLine = (scene, tooth1, tooth2, jaw, value, canvasId = null, startAlignerNum = null, isHighlighted = false, clinicalPair = null) => {
  const positionsResult = computeIPRLinePositions(scene, tooth1, tooth2, jaw);
  if (!positionsResult) return null;

  const { lineStartPoint, endPoint, labelWorldEnd, midpoint } = positionsResult;

  const mesh1FDI = tooth1.fdiNumber != null ? Number(tooth1.fdiNumber) : null;
  const mesh2FDI = tooth2.fdiNumber != null ? Number(tooth2.fdiNumber) : null;

  let toothPair;
  let tooltipTooth1;
  let tooltipTooth2;
  let posteriorsFDI1;
  let posteriorsFDI2;

  if (
    clinicalPair &&
    clinicalPair.tooth1 != null &&
    clinicalPair.tooth2 != null &&
    clinicalPair.tooth1 !== "" &&
    clinicalPair.tooth2 !== ""
  ) {
    const c1 = parseInt(String(clinicalPair.tooth1), 10);
    const c2 = parseInt(String(clinicalPair.tooth2), 10);
    toothPair = [c1, c2].filter((n) => !Number.isNaN(n)).sort((a, b) => a - b).join("-") || "unknown";
    tooltipTooth1 = String(clinicalPair.tooth1);
    tooltipTooth2 = String(clinicalPair.tooth2);
    posteriorsFDI1 = c1;
    posteriorsFDI2 = c2;
  } else {
    toothPair = [mesh1FDI, mesh2FDI].filter((n) => n != null && !Number.isNaN(n)).sort((a, b) => a - b).join("-") || "unknown";
    tooltipTooth1 = mesh1FDI != null ? String(mesh1FDI) : "";
    tooltipTooth2 = mesh2FDI != null ? String(mesh2FDI) : "";
    posteriorsFDI1 = mesh1FDI;
    posteriorsFDI2 = mesh2FDI;
  }

  // Stable id by tooth pair so the same label is reused across aligner changes
  const labelId = `${canvasId}-${toothPair}`;
  const tooltipData = {
    tooth1: tooltipTooth1,
    tooth2: tooltipTooth2,
    value,
    startAlignerNumber: startAlignerNum,
  };
  const label = createIPRLabel(value, labelId, canvasId, tooltipData);
  updateIPRLabelHighlight(label, isHighlighted);

  // Create line geometry
  const positions = [
    lineStartPoint.x, lineStartPoint.y, lineStartPoint.z,
    endPoint.x, endPoint.y, endPoint.z
  ];

  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(positions);

  // Line always uses the default dark gradient — highlight is applied to the label span only
  const { startRGB, endRGB } = IPR_COLORS.default;
  const colors = new Float32Array([...startRGB, ...endRGB]);
  geometry.setColors(colors);

  // Create line material with vertex colors enabled
  const material = new LineMaterial({
    color: 0xffffff, // Base color (will be overridden by vertex colors)
    linewidth: IPR_CONFIG.lineWidth,
    transparent: false,
    opacity: 1.0,
    depthTest: true, // Enable depth testing so lines are hidden when obstructed
    depthWrite: true, // Write to depth buffer
    vertexColors: true, // Enable vertex colors for gradient
  });
  material.resolution.set(window.innerWidth, window.innerHeight);

  // Create line
  const line = new LineSegments2(geometry, material);

  // Determine which quadrant and horizontal section this IPR line is in
  const quadrant = getIPRQuadrant(midpoint, jaw);
  const horizontalSection = getIPRHorizontalSection(midpoint);
  
  // Determine if this is a posterior (back) IPR line
  const isPosterior = isPosteriorIPR(posteriorsFDI1, posteriorsFDI2);

  // Make sure line is visible initially
  line.visible = true;

  // Store IPR value as userData for potential future use
  line.userData = {
    iprValue: value,
    tooth1: tooth1,
    tooth2: tooth2,
    material: material,
    type: 'ipr-line',
    quadrant: quadrant,
    horizontalSection: horizontalSection,
    isPosterior: isPosterior
  };

  // Store line in the map for visibility updates (toothPair used to update in place on aligner change)
  if (canvasId) {
    if (!iprLines.has(canvasId)) {
      iprLines.set(canvasId, []);
    }
    iprLines.get(canvasId).push({ line, quadrant, horizontalSection, midpoint, endPoint: labelWorldEnd, label, isPosterior, toothPair, startAligner: startAlignerNum });
  }

  // Update resolution on window resize
  const updateResolution = () => {
    material.resolution.set(window.innerWidth, window.innerHeight);
  };
  window.addEventListener("resize", updateResolution);

  // Store cleanup function
  line.userData.cleanup = () => {
    window.removeEventListener("resize", updateResolution);
  };

  scene.add(line);

  // Initial label position update (after line is in scene so geometry/matrix are valid)
  if (canvasId && label) {
    const { camera, renderer } = canvasManager.get(canvasId);
    if (camera && renderer) {
      if (isMobilePortrait()) {
        positionMobileIPRLabelFromLineEnd(label, line, camera, renderer, labelWorldEnd);
      } else {
        updateLabelPosition(label, labelWorldEnd, camera, renderer);
      }
    }
  }

  return line;
};

/**
 * Update only the vertex colors of an existing IPR line.
 * (Reserved for future use; line color is currently always the default dark gradient.)
 */
const updateIPRLineColors = (line, isHighlighted) => {
  if (!line?.geometry) return;
  const { startRGB, endRGB } = isHighlighted ? IPR_COLORS.highlighted : IPR_COLORS.default;
  const colors = new Float32Array([...startRGB, ...endRGB]);
  line.geometry.setColors(colors);
  if (line.geometry.attributes.instanceColorStart) {
    line.geometry.attributes.instanceColorStart.needsUpdate = true;
  }
  if (line.geometry.attributes.instanceColorEnd) {
    line.geometry.attributes.instanceColorEnd.needsUpdate = true;
  }
};

/**
 * Apply or remove the blue highlight on the diamond label span.
 * Highlighted = blue gradient background (IPR performed at exactly this aligner).
 * Default     = white background.
 */
const updateIPRLabelHighlight = (label, isHighlighted) => {
  if (!label) return;
  const diamond = label.querySelector('span');
  if (!diamond) return;
  if (isHighlighted) {
    diamond.style.background = 'linear-gradient(135deg, #8CCEE6, #6FC3DF)';
  } else {
    diamond.style.background = '#ffffff';
  }
};

/**
 * Update an existing IPR line and label to new positions (same tooth pair, new aligner).
 * Uses translate for label position so the transition animates smoothly.
 */
const updateIPRLineInPlace = (entry, scene, tooth1, tooth2, jaw, canvasId, isHighlighted = false) => {
  const { line, label } = entry;
  const positionsResult = computeIPRLinePositions(scene, tooth1, tooth2, jaw, false);
  if (!positionsResult || !line?.geometry || !label) return;

  const { lineStartPoint, endPoint, labelWorldEnd, midpoint } = positionsResult;

  const positions = [
    lineStartPoint.x, lineStartPoint.y, lineStartPoint.z,
    endPoint.x, endPoint.y, endPoint.z
  ];
  line.geometry.setPositions(positions);
  if (line.geometry.attributes.instanceStart) {
    line.geometry.attributes.instanceStart.needsUpdate = true;
  }
  if (line.geometry.attributes.instanceEnd) {
    line.geometry.attributes.instanceEnd.needsUpdate = true;
  }

  entry.midpoint = midpoint;
  entry.endPoint = labelWorldEnd;

  const { camera, renderer } = canvasManager.get(canvasId) || {};
  if (camera && renderer) {
    // Aligner change: animate label to new position
    if (isMobilePortrait()) {
      positionMobileIPRLabelFromLineEnd(label, line, camera, renderer, labelWorldEnd, true);
    } else {
      updateLabelPosition(label, labelWorldEnd, camera, renderer, true);
    }
  }

  // Update label diamond background to reflect current highlight state
  updateIPRLabelHighlight(label, isHighlighted);
};

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize IPR visualization for a canvas
 * @param {string} canvasId - Canvas ID
 * @param {string} upperAlignerNum - Active upper aligner number (e.g., "00", "01")
 * @param {string} lowerAlignerNum - Active lower aligner number (e.g., "00", "01")
 */
export const initializeIPRVisualization = (canvasId, upperAlignerNum = null, lowerAlignerNum = null) => {
  if (canvasId === "diff-canvas") return;

  // Avoid duplicate IPR: updateIPRPositions runs first (aligner 00); this runs ~500ms later.
  // If we already have entries for this canvas, skip so we don't push duplicate lines/labels.
  if ((iprLines.get(canvasId) || []).length > 0) {
    return;
  }

  const { scene, models, modelGeometries } = canvasManager.get(canvasId) || {};
  if (!scene || !models || !modelGeometries) {
    return;
  }

  const currentAlignerNum = getCurrentAlignerNumber(
    upperAlignerNum,
    lowerAlignerNum
  );

  // Aligner 0 is the passive tray — do not draw IPR lines yet
  if (currentAlignerNum === 0) {
    return;
  }

  // Show ALL IPR entries from every layer so they are visible from aligner 1.
  const allEntries = getAllIprEntriesWithMetadata();

  if (!allEntries.length) {
    return;
  }

  // Process each IPR entry (format: "tooth:value")
  allEntries.forEach(({ entry, startAligner }) => {
    const { tooth1: tooth1Num, tooth2: tooth2Num, value } = parseIPREntry(entry);

    if (!tooth1Num || !tooth2Num) {
      return;
    }

    // Determine jaw based on FDI numbering
    let jaw = null;
    const tooth1 = parseInt(tooth1Num);

    // Determine jaw from tooth numbers (FDI system)
    if (tooth1 >= 11 && tooth1 <= 28) jaw = "UPPER";
    else if (tooth1 >= 31 && tooth1 <= 48) jaw = "LOWER";
    if (!jaw) {
      return;
    }

    // Get the appropriate aligner number for this jaw
    const alignerNum = jaw === "UPPER" ? upperAlignerNum : lowerAlignerNum;

    const pair = findIPRMeshPairForCanvas(canvasId, tooth1Num, tooth2Num, jaw, alignerNum);
    if (!pair) {
      return;
    }
    const { t1, t2, resolved } = pair;

    // Highlight (blue gradient) only at the exact aligner when the IPR is performed
    const isHighlighted = currentAlignerNum === startAligner;
    drawIPRLine(
      scene,
      t1,
      t2,
      jaw || "UPPER",
      value,
      canvasId,
      startAligner,
      isHighlighted,
      resolved
    );
  });

  // Respect global IPR toggle: if IPR is disabled, hide the lines we just created
  if (!iprEnabled) {
    const lines = iprLines.get(canvasId) || [];
    lines.forEach(({ line, label }) => {
      line.visible = false;
      if (label) {
        label.dataset.visible = "false";
        label.style.opacity = "0";
      }
    });
  }
};

/**
 * Update IPR visualization positions when aligner index changes.
 * Reuses existing lines/labels for the same tooth pair and updates their positions with
 * translate so the transition feels natural (no fade out/in).
 * @param {string} canvasId - Canvas ID
 * @param {string} upperAlignerNum - Active upper aligner number (e.g., "00", "01")
 * @param {string} lowerAlignerNum - Active lower aligner number (e.g., "00", "01")
 */
export const updateIPRPositions = (canvasId, upperAlignerNum, lowerAlignerNum) => {
  if (canvasId === "diff-canvas") return;

  if (!upperAlignerNum && !lowerAlignerNum) return;

  const { scene } = canvasManager.get(canvasId) || {};
  if (!scene) return;

  const currentAlignerNum = getCurrentAlignerNumber(upperAlignerNum, lowerAlignerNum);

  // Aligner 0 is the passive tray — hide everything and bail out without drawing
  if (currentAlignerNum === 0) {
    lastAlignerByCanvas.set(canvasId, 0);
    const lines = iprLines.get(canvasId) || [];
    lines.forEach(({ line, label }) => {
      line.visible = false;
      if (label) {
        label.dataset.visible = "false";
        label.style.opacity = "0";
      }
    });
    return;
  }

  lastAlignerByCanvas.set(canvasId, currentAlignerNum);

  // Always work with ALL entries across every layer so every IPR is always visible.
  const allEntries = getAllIprEntriesWithMetadata();
  const newPairsSet = new Set();
  for (const { entry } of allEntries) {
    const { tooth1: t1s, tooth2: t2s } = parseIPREntry(entry);
    if (!t1s || !t2s) continue;
    const t1n = parseInt(t1s, 10);
    let jawForSet = null;
    if (t1n >= 11 && t1n <= 28) jawForSet = "UPPER";
    else if (t1n >= 31 && t1n <= 48) jawForSet = "LOWER";
    if (!jawForSet) continue;
    const alignerForSet =
      jawForSet === "UPPER" ? upperAlignerNum : lowerAlignerNum;
    const pairForSet = findIPRMeshPairForCanvas(
      canvasId,
      t1s,
      t2s,
      jawForSet,
      alignerForSet
    );
    if (!pairForSet) continue;
    const a = parseInt(pairForSet.resolved.tooth1, 10);
    const b = parseInt(pairForSet.resolved.tooth2, 10);
    if (Number.isNaN(a) || Number.isNaN(b)) continue;
    newPairsSet.add([a, b].sort((x, y) => x - y).join("-"));
  }

  const existing = iprLines.get(canvasId) || [];
  const existingByPair = new Map();
  existing.forEach((e) => {
    if (e.toothPair) existingByPair.set(e.toothPair, e);
  });

  for (const { entry: entryStr, startAligner } of allEntries) {
    const { tooth1: tooth1Num, tooth2: tooth2Num, value } = parseIPREntry(entryStr);
    if (!tooth1Num || !tooth2Num) continue;

    let jaw = null;
    const tooth1 = parseInt(tooth1Num, 10);
    if (tooth1 >= 11 && tooth1 <= 28) jaw = "UPPER";
    else if (tooth1 >= 31 && tooth1 <= 48) jaw = "LOWER";
    if (!jaw) continue;

    // Blue highlight only at the exact aligner when the IPR is performed
    const isHighlighted = currentAlignerNum === startAligner;

    const alignerNum = jaw === "UPPER" ? upperAlignerNum : lowerAlignerNum;
    const pair = findIPRMeshPairForCanvas(
      canvasId,
      tooth1Num,
      tooth2Num,
      jaw,
      alignerNum
    );
    if (!pair) continue;

    const { t1, t2, resolved } = pair;
    const toothPair = [parseInt(resolved.tooth1, 10), parseInt(resolved.tooth2, 10)].sort((a, b) => a - b).join("-");
    const existingEntry = existingByPair.get(toothPair);

    if (!t1 || !t2) {
      // Cannot update position — just refresh the label highlight if entry exists
      if (existingEntry) updateIPRLabelHighlight(existingEntry.label, isHighlighted);
      continue;
    }

    if (existingEntry) {
      updateIPRLineInPlace(existingEntry, scene, t1, t2, jaw || "UPPER", canvasId, isHighlighted);
    } else {
      drawIPRLine(
        scene,
        t1,
        t2,
        jaw || "UPPER",
        value,
        canvasId,
        startAligner,
        isHighlighted,
        resolved
      );
    }
  }

  // Remove only duplicate entries (same toothPair with more than one entry in the list).
  // Lines that have no entry in allEntries (e.g. orphan from a prior session) are also cleaned up.
  const canvasData = canvasManager.get(canvasId);
  const sceneForCleanup = canvasData?.scene;

  const afterFilter = (iprLines.get(canvasId) || []).filter(
    (e) =>
      newPairsSet.has(e.toothPair) &&
      (existingByPair.get(e.toothPair) === e || !existingByPair.has(e.toothPair))
  );

  const keptSet = new Set(afterFilter);
  (iprLines.get(canvasId) || []).forEach((e) => {
    if (keptSet.has(e)) return;
    if (sceneForCleanup && e.line) {
      sceneForCleanup.remove(e.line);
      if (e.line.geometry) e.line.geometry.dispose();
      if (e.line.material) e.line.material.dispose();
      if (e.line.userData?.cleanup) e.line.userData.cleanup();
    }
    if (e.label?.parentNode) e.label.parentNode.removeChild(e.label);
  });

  iprLines.set(canvasId, afterFilter);

  if (!iprEnabled) {
    const lines = iprLines.get(canvasId) || [];
    lines.forEach(({ line, label }) => {
      line.visible = false;
      if (label) {
        label.dataset.visible = "false";
        label.style.opacity = "0";
      }
    });
  } else {
    updateIPRVisibility(canvasId);
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Find all IPR lines in the scene for debugging
 */
export const findIPRLines = (canvasId) => {
  const { scene } = canvasManager.get(canvasId);
  const iprLines = [];
  scene.traverse((obj) => {
    if (obj.userData && obj.userData.type === 'ipr-line') {
      iprLines.push(obj);
    }
  });
  return iprLines;
};

/**
 * Alternative approach: Find teeth by searching through scene meshes
 * This is a fallback if tooth mapping doesn't work
 */
export const findTeethBySearch = (canvasId, toothNum, tooth2Num = null, jaw = null) => {
  const { scene, models, modelGeometries } = canvasManager.get(canvasId);
  if (!scene || !models || !modelGeometries) return null;

  // Get the first aligner (usually "00" or "0") - use the active aligner
  const upperModels = models.UPPER;
  const lowerModels = models.LOWER;

  // Try to find in both jaws or specified jaw
  const jawsToSearch = jaw
    ? [{ name: jaw.toUpperCase(), models: jaw === "UPPER" ? upperModels : lowerModels }]
    : [
      { name: "UPPER", models: upperModels },
      { name: "LOWER", models: lowerModels },
    ];

  for (const { name: jawName, models: jawModels } of jawsToSearch) {
    if (!jawModels) continue;

    // Get first aligner (usually "00")
    const alignerKeys = Object.keys(jawModels).sort((a, b) => {
      const numA = parseInt(a) || 0;
      const numB = parseInt(b) || 0;
      return numA - numB;
    });

    if (alignerKeys.length === 0) continue;

    const firstAligner = alignerKeys[0];
    const aligner = jawModels[firstAligner];

    if (!aligner || !aligner.teeth || !aligner.teeth.length) continue;

    const teeth = aligner.teeth;
    const geometries = modelGeometries[jawName]?.[firstAligner]?.teeth;

    if (!geometries || geometries.length === 0) continue;

    // For now, as a fallback, we'll use indices based on tooth number
    // This is a simplified heuristic - in production you'd need proper mapping
    // Try to map FDI number to index (this is a rough approximation)
    const toothIndex = parseInt(toothNum) % 10; // Use last digit as rough index

    if (toothIndex >= 0 && toothIndex < teeth.length) {
      return {
        material: teeth[toothIndex],
        geometry: geometries[toothIndex],
      };
    }

    // If index doesn't work, return first tooth as fallback
    if (teeth.length > 0) {
      return {
        material: teeth[0],
        geometry: geometries[0],
      };
    }
  }

  return null;
};
